import ExifReader from 'exifreader';
import { fromBuffer } from 'file-type';
import { urlToImage } from './image';

// 获取文件类型，兼容file.type为空的情况
export function getFileType(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
        /**
         * TODO，mimeType获取待优化。先默认获取file.type
         * 依赖file-type获取svg的mimeType不同文件也有各种问题，变成application/xml或空的
         */
        if (file.type && file.type === 'image/svg+xml') return resolve(file.type);

        // ATTENTION: safari < 14 不支持 file.arrayBuffer
        const reader = new FileReader();
        reader.onload = async (e) => {
            if (!e.target?.result) return reject(new Error('no result'));

            const fileType = await fromBuffer(new Uint8Array(e.target.result as ArrayBuffer));

            if (!fileType?.mime) return reject(new Error('getFiletype error'));

            return resolve(fileType.mime);
        };
        reader.onerror = reject;
        reader.onabort = reject;
        reader.readAsArrayBuffer(file);
    });
}

// blob转file
export function blobToFile(blob: Blob, name: string): File {
    const { type } = blob;
    return new File([blob], name, { type });
}

// 文件转图片
export function file2Image(file: File): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
        if (!file || !file.type.startsWith('image/')) {
            reject(new Error('Please provide a valid image file.'));
            return;
        }

        const reader = new FileReader();

        // 读取文件完成后触发
        reader.onload = () => {
            const img = new Image();
            img.onload = () => resolve(img);
            img.onerror = (err) => reject(err);
            img.src = reader.result as string;
        };

        // 文件读取失败
        reader.onerror = () => {
            reject(new Error('Error reading file.'));
        };

        // 开始读取文件
        reader.readAsDataURL(file);
    });
}

// IOS13.4.1和chrome81版本以上的浏览器不用手动翻转
let __AutoCanvasNormalize: boolean | undefined;
export const autoCanvasNormalize = () => {
    if (__AutoCanvasNormalize !== undefined) {
        return __AutoCanvasNormalize;
    }

    const ua = navigator.userAgent;

    // 判断 iPhone 操作系统版本
    const iosVersionMatch = ua.match(/iPhone OS (\d+_\d+(_\d+)?)/);
    let iosVersionValid = false;
    if (iosVersionMatch) {
        const versionString = iosVersionMatch[1];
        console.log('ios version:', versionString);
        const [major, minor, patch] = versionString.split('_').map(Number);
        const currentVersion = major * 10000 + minor * 100 + (patch || 0);
        const targetVersion = 13 * 10000 + 4 * 100 + 1; // 13.4.1 -> 13401
        iosVersionValid = currentVersion > targetVersion;
    }

    // 判断 Chrome 内核版本大于 81
    const chromeVersionMatch = ua.match(/Chrome\/(\d+)/);
    let chromeVersionValid = false;
    if (chromeVersionMatch) {
        const chromeVersion = parseInt(chromeVersionMatch[1], 10);
        console.log('chrome version:', chromeVersion);
        chromeVersionValid = chromeVersion > 81;
    }

    // 判断 Safari 版本
    const safariVersionMatch = ua.match(/Version\/(\d+\.\d+)/);
    let safariVersionValid = false;
    if (safariVersionMatch) {
        const safariVersion = parseFloat(safariVersionMatch[1]);
        console.log('safari version:', safariVersion);
        safariVersionValid = safariVersion >= 13.1; // 设定目标版本为 13.1
    }

    // 判断 Firefox 版本
    const firefoxVersionMatch = ua.match(/Firefox\/(\d+)/);
    let firefoxVersionValid = false;
    if (firefoxVersionMatch) {
        const firefoxVersion = parseInt(firefoxVersionMatch[1], 10);
        console.log('firefox version:', firefoxVersion);
        firefoxVersionValid = firefoxVersion >= 78; // 设定目标版本为 78
    }

    __AutoCanvasNormalize = iosVersionValid || chromeVersionValid || safariVersionValid || firefoxVersionValid;
    return __AutoCanvasNormalize;
};

// 修改旋转方向
export function changeOrientation(file: File, orientation: number) {
    // eslint-disable-next-line no-async-promise-executor
    return new Promise<Blob | null>(async (resolve) => {
        // 这个在safari16以下 如果有旋转的会有问题
        // const image = await createImageBitmap(file);
        const blobURL = URL.createObjectURL(file);
        const image = await urlToImage(blobURL);
        URL.revokeObjectURL(blobURL);
        const { width, height } = image;

        console.log('orientation: ', orientation);
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d')!;

        if (autoCanvasNormalize()) {
            // canvas会自动摆正
            canvas.width = width;
            canvas.height = height;
            ctx.drawImage(image, 0, 0, width, height);
        } else {
            // 旋转绘制图片
            switch (orientation) {
                case 3:
                    canvas.width = width;
                    canvas.height = height;
                    ctx.rotate(Math.PI);
                    ctx.drawImage(image, -width, -height, width, height);
                    break;
                case 6:
                    canvas.width = height;
                    canvas.height = width;
                    ctx.rotate(0.5 * Math.PI);
                    ctx.drawImage(image, 0, -height, width, height);
                    break;
                case 8:
                    canvas.width = height;
                    canvas.height = width;
                    ctx.rotate((3 * Math.PI) / 2);
                    ctx.drawImage(image, -width, 0, width, height);
                    break;
                default:
                    canvas.width = width;
                    canvas.height = height;
                    ctx.drawImage(image, 0, 0, width, height);
                    break;
            }
        }

        canvas.toBlob(
            (blob) => {
                resolve(blob);
            },
            file.type === 'image/png' ? 'image/png' : 'image/jpeg',
        );
    });
}

// 获取图片旋转信息
export async function getImageOrientation(arrayBuffer: ArrayBuffer) {
    try {
        const tags = await ExifReader.load(arrayBuffer);
        let orientation = 1;

        if (tags.Orientation?.value) {
            const value = tags.Orientation.value;

            if (typeof value === 'number') {
                // 直接使用数值
                orientation = value;
            } else if (typeof value === 'string') {
                // 转数字
                const parsedValue = parseInt(value, 10);
                if (!isNaN(parsedValue)) {
                    orientation = parsedValue;
                }
            } else if (Array.isArray(value)) {
                // XmpTags[]
                const firstValidValue = (value as any[]).find((v) => typeof v.value === 'number');
                if (
                    firstValidValue !== undefined &&
                    'value' in firstValidValue &&
                    typeof firstValidValue.value === 'number'
                ) {
                    orientation = firstValidValue.value;
                }
            } else if (typeof value === 'object' && value !== null) {
                // XmpTags
                if ('value' in value && typeof (value as any).value === 'number') {
                    orientation = (value as any).value;
                }
            }
        }

        return orientation || 1; // 默认返回 1（标准方向）
    } catch (error) {
        console.error('Error reading EXIF data:', error);
        return 1; // 解析失败返回默认方向
    }
}

function getHeicConvertTargetType(file: File): string {
    const { type } = file;
    if (type && !/^image\/hei[cf]/i.test(type)) {
        return type;
    }
    return 'image/png';
}

/** 文件头为 HEIC 时按声明类型重新编码；声明类型本身为 HEIC 则默认转为 png */
export async function convertHeicIfNeeded(file: File): Promise<File> {
    try {
        if ((await getFileType(file)) !== 'image/heic') return file;

        const targetType = getHeicConvertTargetType(file);
        const heic2any = (await import('heic2any')).default;
        const convertedBlob = (await heic2any({ blob: file, toType: targetType })) as Blob;
        const extension = targetType.split('/')[1] || 'png';
        return blobToFile(convertedBlob, file.name.replace(/\.[^/.]+$/, `.${extension}`));
    } catch {
        return file;
    }
}

// 摆正图片
export async function normalizeImageFile(file: File): Promise<File> {
    if (file.type !== 'image/jpeg') return file;

    const resolvedFile = await convertHeicIfNeeded(file);
    const orientation = await getImageOrientation(await readFileAsArrayBuffer(resolvedFile));

    if (!orientation || orientation === 1) return resolvedFile;

    const blob = await changeOrientation(resolvedFile, orientation);
    return blob ? blobToFile(blob, resolvedFile.name) : resolvedFile;
}

export function readFileAsArrayBuffer(file: File): Promise<ArrayBuffer> {
    if (typeof file.arrayBuffer === 'function') {
        return file.arrayBuffer();
    }

    // ATTENTION: safari < 14 不支持 file.arrayBuffer
    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = (e) => {
            if (e.target?.result) {
                resolve(e.target.result as ArrayBuffer);
            } else {
                reject(new Error('no result'));
            }
        };
        reader.onerror = reject;
        reader.onabort = reject;
        reader.readAsArrayBuffer(file);
    });
}

/**
 * 将 Base64 字符串转换为 ArrayBuffer
 */
export function base64ToArrayBuffer(base64: string): ArrayBuffer {
    const binaryString = atob(base64); // 解码 Base64
    const length = binaryString.length;
    const bytes = new Uint8Array(length);
    for (let i = 0; i < length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
}

export function fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = () => {
            const base64 = reader.result as string;

            resolve(base64);
        };

        reader.onerror = () => reject(new Error('读取文件失败'));
        reader.readAsDataURL(file);
    });
}
